import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate, Link} from 'react-router-dom';
import {useAuth} from './AuthContext';
import {toast} from '../Toast';
import {checkProfileSlugExists} from '../../utils/designerApi';

const loadInterFont = () => {
    if (!document.getElementById('inter-font')) {
        const link = document.createElement('link');
        link.id = 'inter-font';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
        document.head.appendChild(link);
    }
};

const SLUG_REGEX = /^[a-z0-9-]+$/;
const MIN_SLUG_LEN = 2;
const MAX_SLUG_LEN = 50;

export default function SignupPage() {
    useEffect(() => { loadInterFont(); }, []);
    const [profileSlug, setProfileSlug] = useState('');
    const [slugStatus, setSlugStatus] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const {signup} = useAuth();
    const navigate = useNavigate();

    const normalizedSlug = profileSlug
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/^-+|-+$/g, '');
    const isSlugValid = normalizedSlug.length >= MIN_SLUG_LEN && normalizedSlug.length <= MAX_SLUG_LEN && SLUG_REGEX.test(normalizedSlug);

    const checkSlug = useCallback(async () => {
        if (!isSlugValid) {
            setSlugStatus(normalizedSlug.length > 0 ? 'invalid' : null);
            return;
        }
        setSlugStatus('checking');
        const res = await checkProfileSlugExists({ slug: normalizedSlug });
        if (res?.networkError) {
            setSlugStatus('offline');
        } else {
            setSlugStatus(res?.data?.exists ? 'taken' : 'available');
        }
    }, [normalizedSlug, isSlugValid]);

    useEffect(() => {
        if (!isSlugValid) {
            setSlugStatus(normalizedSlug.length > 0 ? 'invalid' : null);
            return;
        }
        const t = setTimeout(() => checkSlug(), 400);
        return () => clearTimeout(t);
    }, [normalizedSlug, isSlugValid, checkSlug]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!isSlugValid) {
            setError('Profile URL must be 2-50 characters: lowercase letters, numbers, hyphens only');
            return;
        }
        if (slugStatus === 'taken') {
            setError('This username is already taken.');
            return;
        }
        if (slugStatus === 'checking') {
            setError('Please wait while we check availability');
            return;
        }
        if (slugStatus === 'offline') {
            setError('Unable to verify username. Please ensure the backend is running and try again.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        setIsLoading(true);
        const result = await signup({ profileSlug: normalizedSlug, email, password });
        setIsLoading(false);
        if (result.ok) {
            toast.success('Account created successfully! Welcome aboard.');
            navigate('/dashboard');
        } else {
            setError(result.error || 'Registration failed');
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '14px',
        fontSize: '15px',
        fontFamily: "'Inter', sans-serif",
        background: '#ffffff',
        border: '1px solid #e5e5e5',
        borderRadius: '0.8rem',
        outline: 'none',
        boxSizing: 'border-box',
    };

    return (
        <>
            <style>{`
                .auth-page { display: grid; grid-template-rows: auto 1fr; min-height: 100vh; place-items: center; padding: 24px; gap: 24px; font-family: 'Inter', sans-serif; background-color: #ffffff; }
                .auth-card { max-width: 420px; width: 100%; padding: 28px 24px; background-color: #ffffff; }
                .auth-form { display: grid; gap: 16px; grid-template-columns: 1fr; }
                @media (min-width: 640px) { .auth-page { padding: 48px; } .auth-card { padding: 32px 40px; } }
            `}</style>
            <div className="auth-page">
                <div className="auth-card">
                    <img src="/images/Layer-1.png" alt="Optimus" style={{width: 'min(180px, 35vw)', height: 'auto', display: 'block', margin: '0 auto 24px'}} />
                    <h2 style={{fontSize: '38px', fontWeight: 700, textAlign: 'center', marginBottom: '16px', color: '#202020'}}>Create Account</h2>
                    {/* <p style={{fontSize: '15px', color: '#4a4a4a', marginBottom: '24px', textAlign: 'center', lineHeight: 1.5}}>
                        Enter your details below to create your{' '}
                        <Link to="/" style={{color: '#0ea5e9', textDecoration: 'underline', fontWeight: 400}}>optimus.ink</Link> account.
                    </p> */}

                    {error && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626', fontSize: '14px', marginBottom: '16px'}}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div>
                            <label style={{display: 'block', fontSize: '13px', color: '#6b7280', marginBottom: '6px'}}>Your profile URL</label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: '#ffffff',
                                border: '1px solid #e5e5e5',
                                borderRadius: '0.8rem',
                                padding: '0 14px',
                            }}>
                                <span style={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: '#1f2937',
                                    flexShrink: 0,
                                }}>optimus.ink/</span>
                                <input
                                    type="text"
                                    placeholder="username"
                                    value={profileSlug}
                                    onChange={(e) => {
                                        let v = e.target.value
                                            .replace(/^https?:\/\//i, '')
                                            .replace(/^www\./i, '')
                                            .toLowerCase()
                                            .replace(/\s+/g, '-')
                                            .replace(/[^a-z0-9-]/g, '');
                                        setProfileSlug(v);
                                    }}
                                    required
                                    autoComplete="username"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    style={{
                                        ...inputStyle,
                                        flex: 1,
                                        border: 'none',
                                        borderRadius: 0,
                                        padding: '14px 0 14px 8px',
                                        minWidth: 0,
                                        background: 'transparent',
                                    }}
                                />
                            </div>
                            {slugStatus === 'taken' && (
                                <div style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#dc2626', marginTop: '6px'}}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                                        <path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                    This username is already taken.
                                </div>
                            )}
                            {slugStatus === 'invalid' && normalizedSlug.length > 0 && (
                                <div style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#dc2626', marginTop: '6px'}}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                                        <path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                    Use 2–50 chars: lowercase, numbers, hyphens only
                                </div>
                            )}
                            {slugStatus === 'checking' && (
                                <p style={{fontSize: '13px', color: '#6b7280', marginTop: '6px'}}>Checking...</p>
                            )}
                            {slugStatus === 'offline' && (
                                <div style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#b45309', marginTop: '6px'}}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0}}>
                                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                                        <path d="M8 5v3M8 10.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                    Unable to verify. Ensure backend is running on localhost:4000.
                                </div>
                            )}
                        </div>

                        <div>
                            <input
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                autoCorrect="off"
                                autoCapitalize="none"
                                inputMode="email"
                                style={inputStyle}
                            />
                        </div>

                        <div>
                            <input
                                type="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                style={inputStyle}
                            />
                        </div>

                        <div>
                            <input
                                type="password"
                                placeholder="Confirm your password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                style={inputStyle}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                padding: '14px',
                                backgroundColor: '#000000',
                                color: '#ffffff',
                                fontSize: '16px',
                                fontWeight: '600',
                                fontFamily: "'Inter', sans-serif",
                                border: 'none',
                                borderRadius: '0.75rem',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                opacity: isLoading ? 0.7 : 1,
                            }}
                            onMouseOver={(e) => { if (!isLoading) e.target.style.backgroundColor = '#1a1a1a'; }}
                            onMouseOut={(e) => { e.target.style.backgroundColor = '#000000'; }}
                        >
                            {isLoading ? 'Creating account...' : 'Sign up'}
                        </button>
                    </form>

                    <p style={{textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#4a4a4a'}}>
                        Already have an account? <Link to="/login" style={{color: '#0ea5e9', textDecoration: 'none', fontWeight: '600'}}>Login</Link>
                    </p>
                </div>
            </div>
        </>
    );
}
